import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { HashLoader } from 'react-spinners';
import { FaCopy } from "react-icons/fa6";
import { MdDelete, MdOutlineAdd, MdOutlineToggleOff, MdOutlineToggleOn } from "react-icons/md";
import ToastNotification, { showToast } from '../../components/toasts/ToastNotification';
import { MdEditSquare } from "react-icons/md";
import { MdSwitchAccount } from "react-icons/md";
import {
    deleteAccountFromProductForAdmin,
    deletePackageFromProductForAdmin,
    getProductForAdmin,
    pinProduct,
    unpinProduct,
    updateAccountProductForAdmin,
    updateTopUpProductForAdmin,
    deleteImageFromProduct,
    addImageToProduct,
    switchPackageImage,
    switchPackageStatus
} from '../../api/product.api';
import { productAttributesStatusText, productTypeText } from '../../utils';
import AddItemModal from '../../components/modal/add-item-modal';
import ConfirmModal from '../../components/modal/confirm-modal';
import './admin-edit-product.css';
import { useRef } from 'react';
import ChangePackageInformationModal from '../../components/modal/change-package-information-modal';

export const AdminEditProduct = () => {
    const { productId } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const [productName, setProductName] = useState("");
    const [productDescription, setProductDescription] = useState("");
    const [productPrice, setProductPrice] = useState(0);
    const [productStock, setProductStock] = useState(0);
    const [productStatus, setProductStatus] = useState("");
    const [isValuable, setIsValuable] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);
    const switchImageRef = useRef(null);
    const [packageIdToUpdate, setPackageIdToUpdate] = useState('');
    const [packageIdToModify, setPackageIdToModify] = useState('');
    const [packageName, setPackageName] = useState('');
    const [packagePrice, setPackagePrice] = useState(0);

    const [isModalOpen, setIsModalOpen] = useState(false); // Add item modal
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false); // Delete item modal
    const [isDeleteImageModalOpen, setIsDeleteImageModalOpen] = useState(false); // Delete image modal
    const [isPackageChangeModalOpen, setIsPackageChangeModalOpen] = useState(false);
    const [isValuableConfirmModalOpen, setIsValuableConfirmModalOpen] = useState(false);
    const [isNeedAccountModalOpen, setIsNeedAccountModalOpen] = useState(false);

    const [selectedItemToDelete, setSelectedItemToDelete] = useState("");

    useEffect(() => {
        document.title = `Admin - ${productId}`;
        fetchProductData();
    }, [productId]);

    const fetchProductData = async () => {
        setLoading(true);
        try {
            const res = await getProductForAdmin(productId);

            if (res?.success) {
                setProduct(res.data.product);

                setProductName(res.data.product.product_name);
                setProductDescription(res.data.product.product_description);
                setProductStatus(res.data.product.product_status);
                setProductStock(res.data.product.product_stock);
                setIsValuable(res.data.product.isValuable);

                if (res.data.product.product_type === 'game_account' ||
                    res.data.product.product_type === 'utility_account' ||
                    res.data.product.product_type === 'gift_card'
                ) {
                    setProductPrice(res.data.product.product_attributes.price);
                }
            }
        }
        catch (error) {
            navigate("/super-admin/products");
            showToast(error.message, "error");
        }
        finally {
            setLoading(false);
        }
    }

    const copyToClipboard = async (text) => {
        const permission = await navigator.permissions.query({ name: "clipboard-write" });
        if (permission.state === "denied") {
            console.warn("Clipboard permission denied.");
            return;
        }

        await navigator.clipboard.writeText(text);
        showToast("Đã copy dữ liệu", "success");
    };

    const handleSwitchButtonClick = (packageId) => {
        switchImageRef.current.click();
        setPackageIdToUpdate(packageId);
    }

    const handleIsValuableChange = (value) => {
        const nextIsValuable = value === 'true';

        if (product?.product_type === 'game_account' && !isValuable && nextIsValuable) {
            setIsValuableConfirmModalOpen(true);
            return;
        }

        if (product?.product_type === 'game_account' && isValuable && !nextIsValuable) {
            setIsNeedAccountModalOpen(true);
            return;
        }

        setIsValuable(nextIsValuable);
    };

    const handleConfirmSwitchToManualMode = () => {
        setIsValuableConfirmModalOpen(false);
        setIsValuable(true);
        showToast("Đã chuyển sang up thông tin thủ công. Bấm Cập nhật để lưu.", "info");
    };

    const handleConfirmSwitchToAccountMode = async () => {
        setIsNeedAccountModalOpen(false);
        setIsValuable(false);
    };

    const handleUpdateProduct = async () => {
        setLoading(true);
        try {
            let res = null;
            if (product?.product_type === 'game_account') {
                res = await updateAccountProductForAdmin(
                    productId,
                    productName,
                    productDescription,
                    productStatus,
                    productPrice,
                    productStock,
                    isValuable
                );
            } else if (product?.product_type === 'utility_account' || product?.product_type === 'gift_card') {
                res = await updateAccountProductForAdmin(
                    productId,
                    productName,
                    productDescription,
                    productStatus,
                    productPrice,
                    1,
                    isValuable
                );
            }
            else if (product?.product_type === 'topup_package') {
                res = await updateTopUpProductForAdmin(
                    productId,
                    productName,
                    productDescription,
                    productStatus,
                );
            }

            if (res.success) {
                showToast("Cập nhật sản phẩm thành công", "success");
                fetchProductData();
            }
        } catch (error) {
            showToast(error.message, "error");
        }
        finally {
            setLoading(false);
        }
    }

    const handleDeleteProductAttributes = async () => {
        setLoading(true);
        setIsConfirmModalOpen(false);
        try {
            let res = null;
            if (product?.product_type === 'utility_account' ||
                product?.product_type === 'game_account' ||
                product?.product_type === 'gift_card'
            ) {
                res = await deleteAccountFromProductForAdmin(productId, selectedItemToDelete);
                setProduct({
                    ...product,
                    product_attributes: {
                        ...product.product_attributes,
                        account: res.data.updatedAccounts
                    }
                })
            } else if (product?.product_type === 'topup_package') {
                res = await deletePackageFromProductForAdmin(productId, selectedItemToDelete);
                setProduct({
                    ...product,
                    product_attributes: {
                        ...product.product_attributes,
                        packages: res.data.updatedPackages
                    }
                })
            }

            if (res?.success) {
                showToast("Xóa thành công", "success");
            }
        } catch (error) {
            showToast(error.message, "error");
        }
        finally {
            setLoading(false);
        }
    }

    const handlePinProduct = async () => {
        try {
            setLoading(true);

            const res = await pinProduct(product?.productId);

            if (res.success) {
                showToast("Ghim sản phẩm thành công", "success");
                fetchProductData();
            }
        } catch (error) {
            showToast(error.message, "error");
        }
        finally {
            setLoading(false);
        }
    }

    const handleUnpinProduct = async () => {
        try {
            setLoading(true);

            const res = await unpinProduct(product?.productId);

            if (res.success) {
                showToast("Bỏ ghim sản phẩm thành công", "success");
                fetchProductData();
            }
        } catch (error) {
            showToast(error.message, "error");
        }
        finally {
            setLoading(false);
        }
    }

    const handleFileChange = async(event) => {
        const file = event.target.files[0];
        if (file) {
            try {
                setLoading(true);

                const res = await switchPackageImage(product.productId, packageIdToUpdate, file);

                if (res.success) {
                    fetchProductData();

                    showToast("Cập nhật ảnh thành công", "success");
                }
            } catch (error) {
                showToast(error.message, "error");
            }
            finally {
                setLoading(false);
            }
        }
    };

    const handleSwitchPackageStatus = async (packageId, currentStatus) => {
        const nextStatus = currentStatus === 'available' ? 'unavailable' : 'available';

        try {
            setLoading(true);

            const res = await switchPackageStatus(product?.productId, packageId, nextStatus);

            if (res?.success) {
                showToast("Cập nhật trạng thái gói nạp thành công", "success");

                if (res?.data?.product) {
                    setProduct(res.data.product);
                } else {
                    fetchProductData();
                }
            }
        } catch (error) {
            showToast(error.message, "error");
        } finally {
            setLoading(false);
        }
    }

    const handleDeleteImage = async () => {
        try {
            setLoading(true);
            setIsDeleteImageModalOpen(false);

            const res = await deleteImageFromProduct(productId, imageToDelete);

            if (res?.success) {
                showToast("Xóa ảnh thành công", "success");
                // Update product state by removing the deleted image
                setProduct({
                    ...product,
                    product_imgs: res.data.product.product_imgs
                });
            }
        } catch (error) {
            showToast(error.message, "error");
        } finally {
            setLoading(false);
        }
    }

    const handleAddImage = async (e) => {
        e.preventDefault();
        const fileInput = document.getElementById('image-upload');
        if (fileInput.files.length === 0) {
            showToast("Vui lòng chọn ảnh", "error");
            return;
        }

        const formData = new FormData();
        formData.append('product_img', fileInput.files[0]);
        formData.append('productId', productId);

        try {
            setLoading(true);
            const res = await addImageToProduct(formData);

            if (res?.success) {
                showToast("Thêm ảnh thành công", "success");
                // Update product state by adding the new image
                setProduct({
                    ...product,
                    product_imgs: res.data.product.product_imgs
                });

                // Reset file input
                fileInput.value = '';
            }
        } catch (error) {
            showToast(error.message, "error");
        } finally {
            setLoading(false);
        }
    }

    const productStatusLabel = product?.product_status === 'active' ? 'Hoạt động' : 'Không hoạt động';
    const getProductStatusBadgeClass = (status) =>
        `admin-edit-product-status-badge ${status === 'active' ? 'is-active' : 'is-inactive'}`;
    const getSoldBadgeClass = (sold) =>
        `admin-edit-product-status-badge ${sold ? 'is-sold' : 'is-available'}`;
    const getPackageStatusBadgeClass = (status) =>
        `admin-edit-product-status-badge ${status === 'available' ? 'is-available' : 'is-inactive'}`;

    if (loading) {
        return (
            <div className="loader-container">
                <HashLoader color="var(--hash-loader-color)" />
            </div>
        )
    }

    return (
        <section className='admin-edit-product-page'>
            {/* Hidden file input */}
            <input
                type="file"
                accept="image/*"
                ref={switchImageRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
            />
            <div className='admin-edit-product-shell'>
                <header className='admin-edit-product-head'>
                    <div>
                        <p className='admin-edit-product-breadcrumb'>
                            <Link to='/super-admin/products'>Quản trị / Sản phẩm</Link> / Chỉnh sửa
                        </p>
                        <h1 className='admin-edit-product-title'>{product?.product_name}</h1>
                    </div>
                    <div className='admin-edit-product-head-actions'>
                        <button onClick={handleUpdateProduct} className='admin-edit-product-btn admin-edit-product-btn-primary'>
                            Cập nhật
                        </button>
                        <button onClick={() => navigate('/super-admin/products')} className='admin-edit-product-btn admin-edit-product-btn-ghost'>
                            Hủy
                        </button>
                        {product?.product_type === 'game_account' && (
                            product?.product_attributes?.pin ? (
                                <button onClick={handleUnpinProduct} className='admin-edit-product-btn admin-edit-product-btn-warning'>
                                    Bỏ ghim
                                </button>
                            ) : (
                                <button onClick={handlePinProduct} className='admin-edit-product-btn admin-edit-product-btn-success'>
                                    Ghim
                                </button>
                            )
                        )}
                    </div>
                </header>

                <div className='admin-edit-product-layout'>
                    <section className='admin-edit-product-card admin-edit-product-preview-card'>
                        <img
                            loading='lazy'
                            className='admin-edit-product-main-image'
                            src={product?.product_imgs?.[0]}
                            alt='Ảnh sản phẩm'
                        />
                        <div className='admin-edit-product-preview-meta'>
                            <span>{productTypeText(product?.product_type)}</span>
                            <span className={getProductStatusBadgeClass(product?.product_status)}>{productStatusLabel}</span>
                        </div>
                    </section>

                    <section className='admin-edit-product-card'>
                        <h3 className='admin-edit-product-card-title'>Thông tin sản phẩm</h3>
                        <div className='admin-edit-product-form'>
                            <div className='admin-edit-product-field'>
                                <label>Mã sản phẩm</label>
                                <div className='admin-edit-product-input-group'>
                                    <input className='admin-edit-product-input' value={product?.productId || ''} disabled />
                                    <button type='button' className='admin-edit-product-icon-btn' onClick={() => copyToClipboard(product?.productId)}>
                                        <FaCopy />
                                    </button>
                                </div>
                            </div>

                            <div className='admin-edit-product-field'>
                                <label>Tên sản phẩm</label>
                                <input
                                    className='admin-edit-product-input'
                                    value={productName}
                                    onChange={(e) => setProductName(e.target.value)}
                                />
                            </div>

                            <div className='admin-edit-product-field'>
                                <label>Mô tả</label>
                                <textarea
                                    className='admin-edit-product-textarea'
                                    value={productDescription}
                                    onChange={(e) => setProductDescription(e.target.value)}
                                />
                            </div>

                            {(product?.product_type === 'game_account' ||
                                product?.product_type === 'utility_account' ||
                                product?.product_type === 'gift_card'
                            ) && (
                                <div className='admin-edit-product-field'>
                                    <label>Giá</label>
                                    <input
                                        type='text'
                                        className='admin-edit-product-input'
                                        value={Number(productPrice || 0).toLocaleString('vi-VN')}
                                        onChange={(e) => setProductPrice(Number(e.target.value.replace(/\D/g, '')))}
                                    />
                                </div>
                            )}

                            {(product?.product_type === 'game_account' && isValuable) && (
                                <div className='admin-edit-product-field'>
                                    <label>Tồn kho</label>
                                    <input
                                        type='text'
                                        className='admin-edit-product-input'
                                        value={Number(productStock || 0).toLocaleString('vi-VN')}
                                        onChange={(e) => setProductStock(Number(e.target.value.replace(/\D/g, '')))}
                                    />
                                </div>
                            )}

                            <div className='admin-edit-product-row'>
                                <div className='admin-edit-product-field'>
                                    <label>Loại sản phẩm</label>
                                    <input className='admin-edit-product-input' value={productTypeText(product?.product_type)} disabled />
                                </div>
                                <div className='admin-edit-product-field'>
                                    <label>Danh mục</label>
                                    <input className='admin-edit-product-input' value={product?.product_category || ''} disabled />
                                </div>
                            </div>

                            <div className='admin-edit-product-row'>
                                <div className='admin-edit-product-field'>
                                    <label>Trạng thái</label>
                                    <select
                                        className='admin-edit-product-select'
                                        value={productStatus}
                                        onChange={(e) => setProductStatus(e.target.value)}
                                    >
                                        <option value='active'>Hoạt động</option>
                                        <option value='inactive'>Không hoạt động</option>
                                    </select>
                                </div>
                                {((product?.product_type === 'game_account') || (product?.product_type === 'utility_account')) && (
                                    <div className='admin-edit-product-field'>
                                        <label>Up thông tin thủ công</label>
                                        <select
                                            className='admin-edit-product-select'
                                            value={String(isValuable)}
                                            onChange={(e) => handleIsValuableChange(e.target.value)}
                                        >
                                            <option value='true'>Có</option>
                                            <option value='false'>Không</option>
                                        </select>
                                    </div>
                                )}
                            </div>

                            <div className='admin-edit-product-kpis'>
                                <div className='admin-edit-product-kpi'>
                                    <span>Tồn kho</span>
                                    <strong>
                                        {product?.product_type === 'topup_package'
                                            ? product?.product_attributes?.packages?.length
                                            : product?.product_attributes?.account?.length}
                                    </strong>
                                </div>
                                <div className='admin-edit-product-kpi'>
                                    <span>Đã bán</span>
                                    <strong>{product?.product_sold_amount || 0}</strong>
                                </div>
                                <div className='admin-edit-product-kpi'>
                                    <span>Tổng đánh giá</span>
                                    <strong>{product?.totalReviews || 0}</strong>
                                </div>
                                <div className='admin-edit-product-kpi'>
                                    <span>Rating</span>
                                    <strong>{product?.averageRating || 0}</strong>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                <section className='admin-edit-product-card'>
                    <div className='admin-edit-product-card-head'>
                        <h3 className='admin-edit-product-card-title'>Hình ảnh sản phẩm</h3>
                        <form onSubmit={handleAddImage} className='admin-edit-product-upload-form'>
                            <input
                                type='file'
                                id='image-upload'
                                accept='image/*'
                                className='admin-edit-product-file-input'
                            />
                            <button type='submit' className='admin-edit-product-btn admin-edit-product-btn-primary'>
                                <MdOutlineAdd />
                                Thêm ảnh
                            </button>
                        </form>
                    </div>

                    <div className='admin-edit-product-image-grid'>
                        {product?.product_imgs?.map((img, index) => (
                            <div className='admin-edit-product-image-card' key={index}>
                                <img src={img} alt={`Ảnh sản phẩm ${index + 1}`} className='admin-edit-product-image' />
                                <button
                                    className='admin-edit-product-delete-image'
                                    onClick={() => {
                                        setImageToDelete(img);
                                        setIsDeleteImageModalOpen(true);
                                    }}
                                >
                                    <MdDelete />
                                </button>
                            </div>
                        ))}
                    </div>
                </section>

                {!(product?.product_type === 'game_account' && isValuable) && (
                <section className='admin-edit-product-card'>
                    <div className='admin-edit-product-card-head'>
                        <h3 className='admin-edit-product-card-title'>{productTypeText(product?.product_type)}</h3>
                        {((product?.product_type === 'game_account' && !isValuable) ||
                            product?.product_type === 'utility_account' ||
                            product?.product_type === 'topup_package' ||
                            product?.product_type === 'gift_card'
                        ) && (
                            <button className='admin-edit-product-btn admin-edit-product-btn-primary' onClick={() => setIsModalOpen(true)}>
                                <MdOutlineAdd />
                                Thêm mới
                            </button>
                        )}
                    </div>

                    {product?.product_type === 'game_account' && (
                        <div className='admin-edit-product-table-wrap'>
                            <table className='admin-edit-product-table'>
                                <thead>
                                    <tr>
                                        <th>STT</th>
                                        <th>Tài khoản</th>
                                        <th>Mật khẩu</th>
                                        <th>Tình trạng</th>
                                        <th>Thao tác</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!isValuable ? (
                                        product?.product_attributes?.account?.map((acc, index) => (
                                            <tr key={index}>
                                                <td>{index + 1}</td>
                                                <td>{acc.username}</td>
                                                <td>{acc.password}</td>
                                                <td>
                                                    <span className={getSoldBadgeClass(acc.sold)}>
                                                        {acc.sold ? "Đã bán" : "Chưa bán"}
                                                    </span>
                                                </td>
                                                <td>
                                                    <button
                                                        className='admin-edit-product-table-action danger'
                                                        onClick={() => {
                                                            setIsConfirmModalOpen(true);
                                                            setSelectedItemToDelete(acc._id);
                                                        }}
                                                    >
                                                        <MdDelete />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={5} className='admin-edit-product-table-empty'>Không có tài khoản</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {(product?.product_type === 'utility_account' || product?.product_type === 'gift_card') && (
                        <div className='admin-edit-product-table-wrap'>
                            <table className='admin-edit-product-table'>
                                <thead>
                                    <tr>
                                        <th>STT</th>
                                        <th>Tài khoản</th>
                                        <th>Mật khẩu</th>
                                        <th>Trạng thái</th>
                                        <th>Thao tác</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {product?.product_attributes?.account?.map((acc, index) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td>{acc.username}</td>
                                            <td>{acc.password}</td>
                                            <td>
                                                <span className={getSoldBadgeClass(acc.sold)}>
                                                    {acc.sold ? "Đã bán" : "Chưa bán"}
                                                </span>
                                            </td>
                                            <td>
                                                <button
                                                    className='admin-edit-product-table-action danger'
                                                    onClick={() => {
                                                        setIsConfirmModalOpen(true);
                                                        setSelectedItemToDelete(acc._id);
                                                    }}
                                                >
                                                    <MdDelete />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {product?.product_type === 'topup_package' && (
                        <div className='admin-edit-product-table-wrap'>
                            <table className='admin-edit-product-table'>
                                <thead>
                                    <tr>
                                        <th>STT</th>
                                        <th>Hình ảnh</th>
                                        <th>Tên gói nạp</th>
                                        <th>Giá</th>
                                        <th>Trạng thái</th>
                                        <th>Thao tác</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {product?.product_attributes?.packages?.map((pack, index) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td><img className='admin-edit-product-package-image' src={pack.img} alt={`Gói nạp ${pack.name}`} /></td>
                                            <td>{pack.name}</td>
                                            <td>{pack.price.toLocaleString()}</td>
                                            <td>
                                                <span className={getPackageStatusBadgeClass(pack.status)}>
                                                    {productAttributesStatusText(pack.status)}
                                                </span>
                                            </td>
                                            <td>
                                                <div className='admin-edit-product-table-action-group'>
                                                    <button
                                                        className='admin-edit-product-table-action neutral'
                                                        onClick={() => {
                                                            setPackageIdToModify(pack._id);
                                                            setPackageName(pack.name);
                                                            setPackagePrice(pack.price);
                                                            setIsPackageChangeModalOpen(true);
                                                        }}
                                                    >
                                                        <MdEditSquare />
                                                    </button>
                                                    <button
                                                        className='admin-edit-product-table-action info'
                                                        onClick={() => {
                                                            handleSwitchButtonClick(pack._id);
                                                        }}
                                                    >
                                                        <MdSwitchAccount />
                                                    </button>
                                                    <button
                                                        className='admin-edit-product-table-action info'
                                                        title={pack.status === 'available' ? 'Chuyển sang Order' : 'Chuyển sang Có sẵn'}
                                                        onClick={() => handleSwitchPackageStatus(pack._id, pack.status)}
                                                    >
                                                        {pack.status === 'available' ? <MdOutlineToggleOn /> : <MdOutlineToggleOff />}
                                                    </button>
                                                    <button
                                                        className='admin-edit-product-table-action danger'
                                                        onClick={() => {
                                                            setIsConfirmModalOpen(true);
                                                            setSelectedItemToDelete(pack._id);
                                                        }}
                                                    >
                                                        <MdDelete />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
                )}
            </div>

            <ToastNotification />

            <AddItemModal
                productId={productId}
                isOpen={isModalOpen}
                selectedType={product?.product_type}
                onClose={() => setIsModalOpen(false)}
                setProduct={setProduct}
                product={product}
                onAccountAdded={() => setIsValuable(false)}
            />

            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onConfirm={handleDeleteProductAttributes}
                onClose={() => setIsConfirmModalOpen(false)}
                message={'Bạn có chắc chắn muốn xóa mục này không?'}
                title={'Xác nhận xóa'}
            />

            <ConfirmModal
                isOpen={isDeleteImageModalOpen}
                onConfirm={handleDeleteImage}
                onClose={() => setIsDeleteImageModalOpen(false)}
                message={'Bạn có chắc chắn muốn xóa hình ảnh này không?'}
                title={'Xác nhận xóa'}
            />

            <ConfirmModal
                isOpen={isValuableConfirmModalOpen}
                onConfirm={handleConfirmSwitchToManualMode}
                onClose={() => setIsValuableConfirmModalOpen(false)}
                message={'Chuyển sang "Có" sẽ xóa toàn bộ tài khoản game sau khi bấm Cập nhật. Bạn có muốn tiếp tục?'}
                title={'Xác nhận chuyển chế độ'}
            />

            <ConfirmModal
                isOpen={isNeedAccountModalOpen}
                onConfirm={handleConfirmSwitchToAccountMode}
                onClose={() => setIsNeedAccountModalOpen(false)}
                message={'Chuyển sang "Không" yêu cầu bạn thêm tài khoản game để bán. Bạn có muốn tiếp tục?'}
                title={'Lưu ý thêm tài khoản'}
            />
            <ChangePackageInformationModal
                isOpen={isPackageChangeModalOpen}
                title={"Đổi thông tin gói nạp"}
                onClose={() => setIsPackageChangeModalOpen(false)}
                packageId={packageIdToModify}
                productId={product?.productId}
                pack_name={packageName}
                pack_price={packagePrice}
                fetchData={fetchProductData}
            />
        </section>
    );
}
